import { DOCUMENT, Location, PathLocationStrategy } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';

import { WCH_TOKEN_API_URL, WCH_TOKEN_BASE_URL, WCH_TOKEN_DELIVERY_URL } from '../../services/hub-info/tokens';
import { wchGetAppBaseURL } from '../../utils/wch.utils';
import { HubInfoUrlProvider, UrlConfig } from './../../../api';
import {
  getBaseUrlFromDocument,
  getLinksByRel,
  mergeHubInfo,
  parseURL,
  urlToString,
  wchGetApiUrlFromDeliveryURL,
  wchGetDeliveryUrlFromApiURL,
  wchGetHubInfoFromBaseURL,
  wchGetHubInfoFromLinks,
  wchIsPreviewMode,
  wchUrlFromProvider,
} from './../../../utils';
import { assertIsAbsoluteURL, isAssertOn } from './../../internal/assert';
import { createBaseURL } from './../../utils/url.utils';
import { li } from './../logger/logger.service';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'UrlsService';

/**
 * Implementation of a service that decodes the relevant URLs
 *
 * @dynamic
 */
@Injectable({ providedIn: 'root' })
export class UrlsService implements UrlConfig {
  /**
   * The API URL to content hub
   */
  apiUrl: URL;

  /**
   * The Delivery URL to content hub
   */
  deliveryUrl: URL;

  /**
   * The base URL of the host the application is running on. This can be undefined if the
   * application is rendered standalone as part of the universal renderer.
   */
  baseUrl?: URL;

  /**
   * The base URL of the document, this is the URL that is used as the basis for static resources in the application
   */
  resourceUrl?: URL;

  /** True if the system runs in preview mode, else false. */
  isPreviewMode: boolean;

  constructor(
    @Optional()
    @Inject(WCH_TOKEN_BASE_URL)
    aBaseUrl: HubInfoUrlProvider,
    @Optional()
    @Inject(WCH_TOKEN_API_URL)
    aApiUrl: HubInfoUrlProvider,
    @Optional()
    @Inject(WCH_TOKEN_DELIVERY_URL)
    aDeliveryUrl: HubInfoUrlProvider,
    @Optional()
    @Inject(DOCUMENT)
    aDocument: Document
  ) {
    // test for assertions
    const bAssert = isAssertOn();

    /**
     * Decode the resource base
     */
    const resourceBaseUrl = getBaseUrlFromDocument(aDocument);

    /**
     * Decode the base URL from the configuration or fall back to the global window
     */
    const baseUrl = parseURL(wchGetAppBaseURL(aBaseUrl, aDocument));

    /**
     * TODO remove this!
     *
     * @see https://github.com/angular/angular/issues/25323
     *
     * uhh, patch Angular
     */
    PathLocationStrategy.prototype.prepareExternalUrl = (internal: string) => {
      return Location.joinWithSlash(baseUrl.href, internal);
    };

    // decode the fallback
    const cfgFallback = mergeHubInfo(
      wchGetHubInfoFromBaseURL(baseUrl),
      wchGetHubInfoFromLinks(aDocument)
    );

    // the configured values
    const infoApiUrl = wchUrlFromProvider(aApiUrl);
    const infoDeliveryUrl = wchUrlFromProvider(aDeliveryUrl);

    // get from links
    const linksByRel = getLinksByRel(aDocument);

    // init the base paths
    const apiRoot = createBaseURL(
      infoApiUrl
        ? infoApiUrl
        : infoDeliveryUrl
          ? wchGetApiUrlFromDeliveryURL(infoDeliveryUrl)
          : parseURL(cfgFallback.apiUrl)
    );
    const deliveryRoot = createBaseURL(
      infoDeliveryUrl
        ? infoDeliveryUrl
        : infoApiUrl
          ? wchGetDeliveryUrlFromApiURL(infoApiUrl)
          : parseURL(cfgFallback.deliveryUrl)
    );

    // validate the dependencies
    if (bAssert) {
      // assert our dependencies
      assertIsAbsoluteURL(baseUrl);
      assertIsAbsoluteURL(apiRoot);
      assertIsAbsoluteURL(deliveryRoot);
      assertIsAbsoluteURL(resourceBaseUrl);
    }

    // log the tokens
    li(
      LOGGER,
      'baseUrl',
      urlToString(baseUrl),
      'apiUrl',
      apiRoot,
      'deliveryUrl',
      deliveryRoot,
      'resourceUrl',
      urlToString(resourceBaseUrl)
    );

    // attach
    const that = this;
    that.apiUrl = parseURL(apiRoot);
    that.deliveryUrl = parseURL(deliveryRoot);
    that.baseUrl = baseUrl;
    that.resourceUrl = resourceBaseUrl;
    that.isPreviewMode = wchIsPreviewMode(that.apiUrl);
  }
}
